/**
 * Owner viewing slot form scheduling and location display helpers.
 */

import { DEFAULT_VIEWING_TIMEZONE } from './ownerViewingSlotForm';

export const VIEWING_DURATION_OPTIONS = [15, 30, 45, 60, 90];

export const DEFAULT_VIEWING_DURATION_MINUTES = 30;

export const LOCATION_TYPE_OPTIONS = [
  { value: 'in_person', labelKey: 'owner.viewing_location_type_in_person' },
  { value: 'virtual', labelKey: 'owner.viewing_location_type_virtual' },
  { value: 'phone_call', labelKey: 'owner.viewing_location_type_phone_call' },
  { value: 'other', labelKey: 'owner.viewing_location_type_other' },
];

export const MEETING_LOCATION_OPTIONS = {
  in_person: [
    { value: 'property_entrance', labelKey: 'owner.viewing_meeting_in_person.property_entrance' },
    { value: 'building_lobby', labelKey: 'owner.viewing_meeting_in_person.building_lobby' },
    { value: 'parking_area', labelKey: 'owner.viewing_meeting_in_person.parking_area' },
    { value: 'main_gate', labelKey: 'owner.viewing_meeting_in_person.main_gate' },
    { value: 'nearby_landmark', labelKey: 'owner.viewing_meeting_in_person.nearby_landmark' },
    { value: 'custom', labelKey: 'owner.viewing_meeting_custom' },
  ],
  virtual: [
    { value: 'video_link', labelKey: 'owner.viewing_meeting_virtual.video_link' },
    { value: 'phone_number', labelKey: 'owner.viewing_meeting_virtual.phone_number' },
    { value: 'provided_after_reservation', labelKey: 'owner.viewing_meeting_virtual.provided_after_reservation' },
    { value: 'custom', labelKey: 'owner.viewing_meeting_custom' },
  ],
  phone_call: [
    { value: 'owner_calls_guest', labelKey: 'owner.viewing_meeting_phone_call.owner_calls_guest' },
    { value: 'guest_calls_owner', labelKey: 'owner.viewing_meeting_phone_call.guest_calls_owner' },
    { value: 'phone_details_after_reservation', labelKey: 'owner.viewing_meeting_phone_call.phone_details_after_reservation' },
    { value: 'custom', labelKey: 'owner.viewing_meeting_custom' },
  ],
  other: [
    { value: 'custom', labelKey: 'owner.viewing_meeting_custom' },
  ],
};

export const MEETING_OPTION_FALLBACKS = {
  in_person: {
    property_entrance: 'Property entrance',
    building_lobby: 'Building lobby',
    parking_area: 'Parking area',
    main_gate: 'Main gate',
    nearby_landmark: 'Nearby landmark',
  },
  virtual: {
    video_link: 'Video link',
    phone_number: 'Phone number',
    provided_after_reservation: 'Provided after reservation',
  },
  phone_call: {
    owner_calls_guest: 'Owner will call guest',
    guest_calls_owner: 'Guest should call owner',
    phone_details_after_reservation: 'Phone details provided after reservation',
  },
};

export const SUGGESTED_VIEWING_TIMEZONES = [
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Vienna',
  'Europe/Zurich',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'UTC',
];

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

export function extractListingTimezoneFromInquiry(inquiry) {
  if (!inquiry || typeof inquiry !== 'object') {
    return '';
  }
  const candidates = [
    inquiry.listing_timezone,
    inquiry.property_timezone,
    inquiry.timezone,
    inquiry.listing?.timezone,
    inquiry.listing?.property_timezone,
  ];
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (value) {
      return value;
    }
  }
  return '';
}

export function resolveDefaultViewingTimezone({ listingTimezone, browserTimezone } = {}) {
  const listing = String(listingTimezone || '').trim();
  if (listing) {
    return listing;
  }
  const browser = String(browserTimezone || '').trim();
  if (browser) {
    return browser;
  }
  return DEFAULT_VIEWING_TIMEZONE;
}

export function combineDateAndStartTime(date, startTime) {
  if (!date || !startTime) {
    return '';
  }
  return `${date}T${startTime}`;
}

function formatLocalDateTimeValue(date) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function calculateViewingSlotTimes({ date, startTime, durationMinutes }) {
  const startLocal = combineDateAndStartTime(date, startTime);
  if (!startLocal) {
    return { startLocal: '', endLocal: '', startIso: '', endIso: '' };
  }

  const startDate = new Date(startLocal);
  if (Number.isNaN(startDate.getTime())) {
    return { startLocal, endLocal: '', startIso: '', endIso: '' };
  }

  const duration = Number(durationMinutes);
  if (!Number.isFinite(duration) || duration < 1) {
    return {
      startLocal,
      endLocal: '',
      startIso: startDate.toISOString(),
      endIso: '',
    };
  }

  const endDate = new Date(startDate.getTime() + duration * 60 * 1000);
  return {
    startLocal,
    endLocal: formatLocalDateTimeValue(endDate),
    startIso: startDate.toISOString(),
    endIso: endDate.toISOString(),
  };
}

export function formatViewingEndTimePreview({ date, startTime, durationMinutes }) {
  const times = calculateViewingSlotTimes({ date, startTime, durationMinutes });
  if (!times.endLocal) {
    return '';
  }

  const startDate = new Date(times.startLocal);
  const endDate = new Date(times.endLocal);
  if (Number.isNaN(endDate.getTime())) {
    return '';
  }

  const sameDay = startDate.toDateString() === endDate.toDateString();
  if (sameDay) {
    return endDate.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return endDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getMeetingLocationOptionsForType(locationType) {
  const normalized = String(locationType || '').trim();
  return MEETING_LOCATION_OPTIONS[normalized] || MEETING_LOCATION_OPTIONS.other;
}

export function isCustomMeetingLocationOption(meetingLocationOption) {
  return String(meetingLocationOption || '').trim() === 'custom';
}

export function resolveMeetingLocationForPayload(
  { locationType, meetingLocationOption, meetingLocationCustom },
  t = (key, fallback) => fallback || key,
) {
  const option = String(meetingLocationOption || '').trim();
  if (!option) {
    return '';
  }
  if (option === 'custom') {
    return String(meetingLocationCustom || '').trim();
  }

  const type = String(locationType || '').trim();
  const optionMeta = getMeetingLocationOptionsForType(type).find((entry) => entry.value === option);
  if (!optionMeta) {
    return option;
  }

  const fallback = MEETING_OPTION_FALLBACKS[type]?.[option] || option;
  return t(optionMeta.labelKey, fallback);
}

export const DEFAULT_SCHEDULING_SEARCH_DAYS = 365;

export function getLocalDateString(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseDateOnly(dateStr) {
  const normalized = String(dateStr || '').trim().split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }
  const [year, month, day] = normalized.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) {
    return null;
  }
  return parsed;
}

export function compareDateOnly(left, right) {
  const leftDate = parseDateOnly(left);
  const rightDate = parseDateOnly(right);
  if (!leftDate || !rightDate) {
    return 0;
  }
  if (leftDate.getTime() < rightDate.getTime()) {
    return -1;
  }
  if (leftDate.getTime() > rightDate.getTime()) {
    return 1;
  }
  return 0;
}

export function addDaysToDateString(dateStr, days) {
  const parsed = parseDateOnly(dateStr);
  if (!parsed || !Number.isFinite(days)) {
    return '';
  }
  parsed.setDate(parsed.getDate() + days);
  return getLocalDateString(parsed);
}

export function buildBlockedDateSet(blockedDates = []) {
  const blockedDateSet = new Set();
  for (const entry of blockedDates) {
    const dateValue = typeof entry === 'string' ? entry : entry?.date;
    const normalized = String(dateValue || '').trim().split('T')[0];
    if (normalized) {
      blockedDateSet.add(normalized);
    }
  }
  return blockedDateSet;
}

export function buildViewingSchedulingConstraints({
  blockedDates = [],
  rentedUntil = null,
  isRented = false,
  referenceDate = new Date(),
} = {}) {
  const minDate = getLocalDateString(referenceDate);
  const blockedDateSet = buildBlockedDateSet(blockedDates);
  const rentedUntilDate = rentedUntil ? String(rentedUntil).trim().split('T')[0] : null;

  return {
    minDate,
    blockedDateSet,
    rentedUntil: rentedUntilDate,
    isRented: Boolean(isRented),
  };
}

export function buildViewingSchedulingConstraintsFromCalendar(calendarData, referenceDate = new Date()) {
  return buildViewingSchedulingConstraints({
    blockedDates: calendarData?.blocked_dates || [],
    rentedUntil: calendarData?.rented_until,
    isRented: calendarData?.is_rented,
    referenceDate,
  });
}

export function extractListingIdFromInquiry(inquiry) {
  if (!inquiry || typeof inquiry !== 'object') {
    return '';
  }
  return String(inquiry.listing_id || inquiry.listing?.id || '').trim();
}

export function isViewingDateBlocked(dateStr, constraints) {
  if (!dateStr) {
    return { blocked: true, reason: 'empty' };
  }
  if (!constraints || !parseDateOnly(dateStr)) {
    return { blocked: true, reason: 'invalid' };
  }
  if (compareDateOnly(dateStr, constraints.minDate) < 0) {
    return { blocked: true, reason: 'past' };
  }
  if (constraints.isRented && constraints.rentedUntil && compareDateOnly(dateStr, constraints.rentedUntil) < 0) {
    return { blocked: true, reason: 'rented' };
  }
  if (constraints.blockedDateSet?.has(dateStr)) {
    return { blocked: true, reason: 'unavailable' };
  }
  return { blocked: false, reason: '' };
}

export function findNextValidViewingDate({
  startDateStr,
  constraints,
  maxDays = DEFAULT_SCHEDULING_SEARCH_DAYS,
}) {
  if (!constraints) {
    return '';
  }

  let candidate = startDateStr || constraints.minDate;
  if (compareDateOnly(candidate, constraints.minDate) < 0) {
    candidate = constraints.minDate;
  }

  for (let dayOffset = 0; dayOffset < maxDays; dayOffset += 1) {
    const dateToCheck = dayOffset === 0 ? candidate : addDaysToDateString(candidate, 1);
    if (!dateToCheck) {
      break;
    }
    if (dayOffset > 0) {
      candidate = dateToCheck;
    }
    const blocked = isViewingDateBlocked(dateToCheck, constraints);
    if (!blocked.blocked) {
      return dateToCheck;
    }
  }

  return '';
}

export function resolveInitialViewingFormDate({
  preferredDate = '',
  constraints,
  dateManuallySet = false,
}) {
  const candidate = preferredDate || constraints?.minDate || '';
  if (!constraints) {
    return candidate;
  }

  if (dateManuallySet && preferredDate) {
    return preferredDate;
  }

  const blocked = isViewingDateBlocked(candidate, constraints);
  if (!blocked.blocked) {
    return candidate;
  }

  return findNextValidViewingDate({
    startDateStr: candidate,
    constraints,
  });
}

export function formatViewingDateLabel(dateStr, locale) {
  const parsed = parseDateOnly(dateStr);
  if (!parsed) {
    return dateStr || '';
  }
  return parsed.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getViewingDateBlockMessage(
  reason,
  t = (key, fallback) => fallback || key,
  { nextValidDate = '' } = {},
) {
  const messages = {
    past: t('owner.viewing_date_blocked_past', 'Choose today or a later date.'),
    unavailable: t('owner.viewing_date_blocked_unavailable', 'This date is unavailable for viewings.'),
    rented: t('owner.viewing_date_blocked_rented', 'This property is not available for viewings until the rental period ends.'),
    invalid: t('owner.viewing_date_invalid', 'Choose a valid date.'),
    empty: t('owner.viewing_date_required', 'Date is required'),
  };

  const baseMessage = messages[reason] || messages.invalid;
  if (!nextValidDate) {
    return baseMessage;
  }

  const nextDateLabel = formatViewingDateLabel(nextValidDate);
  return `${baseMessage} ${t('owner.viewing_date_pick_next', 'Next available date: {date}').replace('{date}', nextDateLabel)}`;
}

export function buildEmptyViewingSlotForm(inquiry, schedulingConstraints = null) {
  const listingTimezone = extractListingTimezoneFromInquiry(inquiry);
  const browserTimezone = getBrowserTimezone();
  const date = resolveInitialViewingFormDate({
    preferredDate: '',
    constraints: schedulingConstraints,
    dateManuallySet: false,
  });

  return {
    date,
    startTime: '',
    durationMinutes: String(DEFAULT_VIEWING_DURATION_MINUTES),
    capacity: '1',
    timezone: resolveDefaultViewingTimezone({ listingTimezone, browserTimezone }),
    locationType: '',
    meetingLocationOption: '',
    meetingLocationCustom: '',
  };
}

export function buildViewingSlotApiBody(form, validationPayload, t = (key, fallback) => fallback || key) {
  const body = { ...validationPayload };

  const locationType = String(form.locationType || '').trim();
  if (locationType) {
    body.location_type = locationType;
  }

  const meetingLocation = resolveMeetingLocationForPayload(
    {
      locationType,
      meetingLocationOption: form.meetingLocationOption,
      meetingLocationCustom: form.meetingLocationCustom,
    },
    t,
  );
  if (meetingLocation) {
    body.meeting_location = meetingLocation;
  }

  return body;
}
